declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

export type FacebookAuthResponse = {
  accessToken: string;
  expiresIn: number;
  signedRequest?: string;
  userID: string;
};

export type FacebookLoginStatusResponse = {
  status: 'connected' | 'not_authorized' | 'unknown';
  authResponse?: FacebookAuthResponse;
};

type FacebookSdk = {
  init: (params: {
    appId: string;
    cookie: boolean;
    xfbml: boolean;
    version: string;
  }) => void;
  ui: (
    params: { method: string; href: string; quote?: string },
    callback?: (response: unknown) => void,
  ) => void;
  getLoginStatus: (
    callback: (response: FacebookLoginStatusResponse) => void,
    force?: boolean,
  ) => void;
  login: (
    callback: (response: FacebookLoginStatusResponse) => void,
    options?: { scope?: string; auth_type?: string },
  ) => void;
  XFBML: {
    parse: (node?: HTMLElement) => void;
  };
  Event: {
    subscribe: (event: string, callback: (response: FacebookLoginStatusResponse) => void) => void;
    unsubscribe: (event: string, callback: (response: FacebookLoginStatusResponse) => void) => void;
  };
};

export type FacebookSdkOptions = {
  xfbml?: boolean;
};

let sdkLoadPromise: Promise<void> | null = null;
let initializedXfbml = false;

/** Loads Facebook JS SDK once (official login button). */
export function loadFacebookSdk(
  appId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '',
  options: FacebookSdkOptions = {},
): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Facebook SDK requires browser'));
  }
  if (!appId) {
    return Promise.reject(new Error('NEXT_PUBLIC_FACEBOOK_APP_ID is not configured'));
  }
  if (window.FB) {
    return Promise.resolve();
  }
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  const xfbml = options.xfbml ?? initializedXfbml;

  sdkLoadPromise = new Promise((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB!.init({ appId, cookie: true, xfbml, version: 'v21.0' });
      initializedXfbml = initializedXfbml || xfbml;
      resolve();
    };

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Facebook SDK failed to load'));
    document.body.appendChild(script);
  });

  return sdkLoadPromise;
}

/** Open Facebook Login dialog and return the auth response. */
export async function loginWithFacebook(
  appId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '',
  scope = 'email,public_profile',
): Promise<FacebookLoginStatusResponse> {
  await loadFacebookSdk(appId);
  if (!window.FB) {
    throw new Error('Facebook SDK not available');
  }
  return new Promise((resolve, reject) => {
    window.FB!.login(
      (response) => {
        if (response.status === 'connected' && response.authResponse?.accessToken) {
          resolve(response);
          return;
        }
        reject(new Error('Facebook login cancelled'));
      },
      { scope },
    );
  });
}

/** Parse XFBML (e.g. fb-login-button) inside a container. */
export async function parseFacebookXfbml(
  container: HTMLElement,
  appId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '',
): Promise<void> {
  await loadFacebookSdk(appId, { xfbml: true });
  window.FB?.XFBML.parse(container);
}

export function subscribeFacebookLogin(
  handler: (response: FacebookLoginStatusResponse) => void,
): () => void {
  if (!window.FB) return () => undefined;
  const wrapped = (response: FacebookLoginStatusResponse) => handler(response);
  window.FB.Event.subscribe('auth.login', wrapped);
  return () => window.FB?.Event.unsubscribe('auth.login', wrapped);
}
